import { useState, useEffect, useRef } from "react";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CONTACT_INFO } from "@/lib/index";

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: "Home", href: "#hero" },
    { label: "Promozione", href: "#promozione" },
    { label: "Camerette", href: "#camerette" },
    { label: "Perché Sceglierci", href: "#perche-sceglierci" },
    { label: "Showroom", href: "#showroom" },
    { label: "Contatti", href: "#contatti" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 ${
          scrolled
            ? "bg-background/95 backdrop-blur-sm shadow-lg"
            : "bg-background/80 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="flex items-center"
            >
              <img 
                src="/images/Logo Mobilandia contorno bianco.png" 
                alt="Mobilandia Logo" 
                className="w-36 h-36 object-contain"
              />
            </motion.div>

            <nav className="hidden lg:flex items-center gap-8">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.href}
                  href={item.href}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="text-sm font-medium text-foreground/80 hover:text-primary transition-colors relative group"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-200 group-hover:w-full" />
                </motion.a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-4">
              <a
                href={`https://wa.me/${CONTACT_INFO.phone}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Phone className="w-4 h-4" />
                <span className="font-medium">{CONTACT_INFO.phone}</span>
              </a>
              <Button
                asChild
                className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105"
              >
                <a href="https://mobilandiaemilia.it/appuntamento-negozio/" target="_blank" rel="noopener noreferrer">Prenota un Appuntamento</a>
              </Button>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-foreground" />
              ) : (
                <Menu className="w-6 h-6 text-foreground" />
              )}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden border-t border-border bg-background/95 backdrop-blur-sm"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="text-base font-medium text-foreground/80 hover:text-primary transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="pt-4 border-t border-border space-y-3">
                  <a
                    href={`https://wa.me/${CONTACT_INFO.phone}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{CONTACT_INFO.phone}</span>
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span>{CONTACT_INFO.email}</span>
                  </a>
                  <Button
                    asChild
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                  >
                    <a href="https://mobilandiaemilia.it/appuntamento-negozio/" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)}>
                      Prenota un Appuntamento
                    </a>
                  </Button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }}>{children}</main>

      <footer className="bg-card border-t border-border">
        <div className="container mx-auto px-4 py-16">
          {/* Logo e descrizione */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <img 
                src="/images/Logo Mobilandia contorno bianco.png" 
                alt="Mobilandia Logo" 
                className="w-24 h-24 object-contain"
              />
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
              Oltre 50 anni di esperienza nell'arredamento. Leader in Emilia-Romagna per camerette, cucine e arredamento completo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={`https://wa.me/${CONTACT_INFO.phone}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-600 hover:text-green-700 font-medium transition-colors"
              >
                <Phone className="w-5 h-5" />
                <span>{CONTACT_INFO.phone}</span>
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary font-medium transition-colors"
              >
                <Mail className="w-5 h-5" />
                <span>{CONTACT_INFO.email}</span>
              </a>
            </div>
          </div>

          {/* Link rapidi e copyright */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-8 border-t border-border">
            <div>
              <h4 className="font-semibold text-foreground mb-4">Link Rapidi</h4>
              <nav className="grid grid-cols-2 gap-2">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
            
            <div className="text-right">
              <p className="text-sm text-muted-foreground mb-2">
                Mobilandia Emilia - Arredamento e Design
              </p>
              <p className="text-xs text-muted-foreground">
                © 2026 Mobilandia. Tutti i diritti riservati.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                P.IVA: 12345678901 - REA: MO-123456
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
